import extractFiles from 'extract-files/extractFiles.mjs';
import isExtractableFile from 'extract-files/isExtractableFile.mjs';
import type { GraphQLError } from 'graphql';
import { GraphQLClient, type RequestMiddleware } from 'graphql-request';
import { createClient, type Client } from 'graphql-ws';
import { config } from '@/config';
import { Sentry } from '@/lib';

// graphql-request v7 requires an absolute URL; default to the page
// origin when VITE_API_URL is not set (dev, prod, and PR previews all
// serve the API from the same origin as the client).
const apiOrigin = config.api.url || window.location.origin;

// graphql-request v5 had built-in support for the multipart-request-spec
// (https://github.com/jaydenseric/graphql-multipart-request-spec) and
// silently rewrote requests with File/Blob variables to FormData. v7
// dropped that, so File values now JSON.stringify to `{}` and the server
// rejects them with `Upload value invalid`. Reimplement the spec as a
// requestMiddleware: walk the variables for files, and if any are found,
// replace the JSON body with a multipart FormData body.
const multipartUploadMiddleware: RequestMiddleware = (request) => {
  // Walk the original variables for files. The body has already been
  // JSON.stringified by this point, which means File/Blob values were
  // serialized to `{}` and are unreachable from `request.body` —
  // `request.variables` is the only place the file references survive.
  const { clone: variablesClone, files } = extractFiles(
    request.variables,
    isExtractableFile,
    'variables'
  );
  if (files.size === 0) return request;

  // Recover the query string by parsing the body the client built. Doing
  // this lazily — only when an upload is detected — keeps non-upload
  // requests on the JSON fast path.
  //
  // A non-string body carries no query to recover, and the empty string this
  // used to substitute builds a request the server can only reject: Mercurius
  // answers any body it parses without a `query` with a 400 coded
  // MER_ERR_GQL_PERSISTED_QUERY_NOT_FOUND, which reads as an unrelated
  // persisted-query fault rather than as our own malformed upload. graphql-request
  // always hands the middleware a JSON string for a POST, so failing loudly
  // keeps a regression in that contract legible instead of laundering it
  // through the server as a mystery 400.
  if (typeof request.body !== 'string') {
    throw new Error(
      `Cannot attach uploads to a ${typeof request.body} GraphQL request body`
    );
  }

  const operations = {
    ...(JSON.parse(request.body) as { query: string }),
    variables: variablesClone,
    operationName: request.operationName,
  };

  const form = new FormData();
  form.append('operations', JSON.stringify(operations));

  const map: Record<string, string[]> = {};
  const orderedFiles: (File | Blob)[] = [];
  let i = 0;
  for (const [file, paths] of files) {
    map[String(i)] = paths;
    orderedFiles.push(file);
    i++;
  }
  form.append('map', JSON.stringify(map));
  orderedFiles.forEach((file, index) => {
    form.append(String(index), file);
  });

  const headers = new Headers(request.headers);
  headers.delete('content-type');

  return { ...request, body: form, headers };
};

export const gqlClient = new GraphQLClient(`${apiOrigin}/api/graphql`, {
  credentials: 'include',
  mode: 'cors',
  requestMiddleware: multipartUploadMiddleware,
});

// graphql-ws's `createClient` eagerly resolves a WebSocket implementation and
// throws `WebSocket implementation missing` synchronously at construction time
// when the runtime exposes no global `WebSocket`. That happens in locked-down
// in-app WebViews, crawlers/bots spoofing a browser UA, and other non-browser
// contexts. Because `gqlWsClient` is created at module scope, that throw would
// take down this entire module — and every page that imports it — for those
// visitors, surfacing as an uncaught error at page load. Real-time
// subscriptions are a progressive enhancement, so when there's no WebSocket we
// return an inert client that no-ops `subscribe` (still handing back the
// expected unsubscribe function) instead of crashing the app. Guarding here also
// avoids ever reaching graphql-ws' throwing constructor (Sentry WEB-CLIENT-24P).
export const createGqlWsClient = (): Client => {
  if (typeof WebSocket === 'undefined') {
    const noop = () => {};
    return {
      on: () => noop,
      subscribe: () => noop,
      iterate: async function* () {},
      dispose: noop,
      terminate: noop,
    } as unknown as Client;
  }

  return createClient({
    url: `${config.websockets.url}/api/graphql`,
  });
};

export const gqlWsClient = createGqlWsClient();

const isCloseEvent = (value: unknown): value is CloseEvent => {
  const valueAsCloseEvent = value as CloseEvent;
  return Boolean(valueAsCloseEvent.code && valueAsCloseEvent.reason);
};

/*
 * Besides the errors being `Error` and `readonly GraphQLError[]`, it
 * can also be a `CloseEvent`, but to avoid bundling DOM typings because
 * the client can run in Node env too, you should assert the close event
 * type during implementation.
 */
export const handleError = (error: unknown): void => {
  if (isCloseEvent(error)) {
    Sentry.captureException(
      new Error(`Websocket closed: ${error.code}, ${error.reason}`)
    );
  } else if (Array.isArray(error)) {
    error.map((e: GraphQLError) => Sentry.captureException(e));
  } else if (error instanceof Error) {
    Sentry.captureException(error);
  }
};
